import VectorLayer from 'ol/layer/Vector.js';
import type {Options as VectorSourceOptions} from 'ol/source/Vector';
import VectorSource from 'ol/source/Vector.js';
import ClusterSource from 'ol/source/Cluster.js';
import {Z_INDEXES} from '../constants.ts';
import {proj as epsg21781} from '@geoblocks/proj/src/EPSG_21781.js';
import GeoJSONFormat from 'ol/format/GeoJSON.js';
import {tile as tileLoadingStrategy} from 'ol/loadingstrategy.js';
import TileGrid from 'ol/tilegrid/TileGrid.js';
import {extentToEPSG21781} from '../proj.ts';
import {BASE_API_URL} from '../../envs.ts';
import {Icon, Style} from 'ol/style.js';
import type {Extent} from 'ol/extent';
import type {Options as VectorLayerOptions} from 'ol/layer/Vector';
import {getLanguageCode} from '../../i18n.ts';
import {activeFocusGallery$} from '../../stores/store-main.ts';
import type {RouteSegmentGallery} from '../../apis/api-segments.ts';
import type {FeatureLike} from 'ol/Feature';
import type Feature from 'ol/Feature';

export class PhotoSource extends VectorSource {
  constructor(land: string, options?: VectorSourceOptions<Feature>) {
    const tileGrid = new TileGrid({
      extent: epsg21781.getExtent(),
      resolutions: [10],
      tileSize: 1024,
    });

    super(
      Object.assign(
        {},
        {
          format: new GeoJSONFormat({
            dataProjection: epsg21781,
          }),
          strategy: tileLoadingStrategy(tileGrid),
          url: (extent: Extent) =>
            `${BASE_API_URL}/api/4/photo?land=${land}&lang=${getLanguageCode()}&bbox=${extentToEPSG21781(
              extent,
            ).join(',')}`,
        },
        options,
      ),
    );
  }
}

export class PhotoLayer extends VectorLayer<VectorSource> {
  constructor(land: string, name: string, options?: VectorLayerOptions) {
    const clusterSource = new ClusterSource({
      distance: 40,
      source: new PhotoSource(land),
    });

    const iconStyle = new Style({
      image: new Icon({
        src: '/img/camera.svg?ts11=TIMESTAMP11',
      }),
    });

    const transparentIconStyle = new Style({
      image: new Icon({
        src: '/img/camera.svg?ts11=TIMESTAMP11',
        opacity: 0.5,
      }),
    });

    const focusStyle = (
      feature: FeatureLike,
      gallery: RouteSegmentGallery[],
    ) => {
      const features = feature.get('features');
      if (!features || !features[0]) return null;
      const featureId = features[0].get('id');
      return gallery.find((p) => featureId === p.id)
        ? iconStyle
        : transparentIconStyle;
    };

    super(
      Object.assign(
        {},
        {
          zIndex: Z_INDEXES.ROUTE_PHOTOS,
          visible: false,
          maxResolution: 10,
          source: clusterSource,
          style: iconStyle,
          properties: {
            type: 'photo_layer',
            name: `logo_${name}`,
          },
        },
        options,
      ),
    );

    activeFocusGallery$.subscribe((gallery: RouteSegmentGallery[]) => {
      if (gallery) {
        clusterSource.setDistance(0);
        this.setStyle((feature) => focusStyle(feature, gallery));
      } else {
        clusterSource.setDistance(40);
        this.setStyle(iconStyle);
      }
    });
  }
}
